import { Plus, ShieldCheck } from '@phosphor-icons/react';
import { useGetSubscription, useListPlans } from '@reelplicate/api-client/billing';
import type { MemberDto } from '@reelplicate/api-client/model';
import {
  getListMembersQueryKey,
  useListMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from '@reelplicate/api-client/team';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  ConfirmDialog,
  Container,
  Inline,
  Link,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { UsageMeter } from '@/features/settings/usage-meter';

import { InviteModal } from './invite-modal';
import { InviteRow, MemberRow } from './member-rows';
import { memberName } from './names';
import { type Role, roleName, toApiRole } from './roles';
import { RolesTable } from './roles-table';
import { useInvites } from './use-invites';

const BILLING_PATH = '/settings/billing';

type TabId = 'members' | 'invites' | 'roles';

const LIST_CLASS =
  'bg-bg-surface border-border-subtle [&>li:not(:last-child)]:border-border-subtle rounded-lg border px-5 py-1 [&>li:not(:last-child)]:border-b';

/**
 * Figma "Team — Members / Pending invites / Roles & permissions" (328:1770,
 * 328:2091, 328:2342) with the "Invite people" modal (328:2662) and the
 * "Remove member" confirm (328:3024). Members and pending invites both take
 * a seat on the plan. Only owners get the controls; everyone else reads.
 */
export function TeamPage() {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const workspace = workspaces?.[0]?.name ?? 'your workspace';

  const { data: members } = useListMembers(workspaceId ?? '', {
    query: { enabled: Boolean(workspaceId) },
  });
  const {
    invites: { data: invites = [] },
    send,
    resend,
    revoke,
  } = useInvites(workspaceId, BILLING_PATH);
  const { data: plans } = useListPlans();
  // No plan chosen yet (404) just means there's no seat count to show.
  const { data: subscription } = useGetSubscription(workspaceId ?? '', {
    query: { enabled: Boolean(workspaceId), retry: false, throwOnError: false },
  });
  const plan = plans?.find((p) => p.id === subscription?.plan);

  const [tab, setTab] = useState<TabId>('members');
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<MemberDto | null>(null);

  const refreshMembers = () =>
    workspaceId &&
    queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(workspaceId) });
  const updateRole = useUpdateMemberRole({ mutation: { onSuccess: refreshMembers } });
  const removeMember = useRemoveMember({ mutation: { onSuccess: refreshMembers } });

  const canManage = members?.some((m) => m.isYou && m.role === 'OWNER') ?? false;
  const seatsUsed = (members?.length ?? 0) + invites.length;
  const seatsLeft = plan ? plan.seats - seatsUsed : 0;
  const seatCaption =
    seatsLeft > 0
      ? `${seatsLeft} ${seatsLeft === 1 ? 'seat' : 'seats'} left on your plan.`
      : 'All seats are used. Upgrade to invite more people.';

  const changeRole = (member: MemberDto, role: Role) => {
    if (!workspaceId) return;
    const name = memberName(member);
    updateRole.mutate(
      { id: workspaceId, userId: member.userId, data: { role: toApiRole(role) } },
      {
        onSuccess: () =>
          toast.success(
            `${name} is now ${role === 'owner' || role === 'editor' ? 'an' : 'a'} ${roleName(role)}`,
          ),
      },
    );
  };

  const remove = (member: MemberDto) => {
    if (!workspaceId) return;
    removeMember.mutate(
      { id: workspaceId, userId: member.userId },
      {
        onSuccess: () =>
          toast.success(`${memberName(member)} was removed from ${workspace}`),
      },
    );
  };

  const sendInvites = async (emails: string[], role: Role) => {
    const sent = await send(emails, role);
    if (sent) {
      setInviting(false);
      setTab('invites');
    }
    return sent;
  };

  const removingName = removing ? memberName(removing) : '';
  const removingFirst = removingName.split(' ')[0] ?? '';

  return (
    <Container paddingY={10}>
      <Stack gap={6} className="mx-auto max-w-240">
        <Inline gap={3} wrap>
          <Stack gap={1} className="min-w-0 flex-1">
            <h1 className="text-heading-h1 text-text-primary">Team</h1>
            <p className="text-body-md text-text-secondary">
              People in {workspace} and what each of them can do.
            </p>
          </Stack>
          {canManage && (
            <Button leadingIcon={Plus} onClick={() => setInviting(true)}>
              Invite people
            </Button>
          )}
        </Inline>

        {plan && (
          <div className="bg-bg-surface border-border-subtle rounded-lg border px-6 py-5">
            <UsageMeter
              label={`Seats on ${plan.name}`}
              used={seatsUsed}
              limit={plan.seats}
              caption={seatCaption}
              action={
                canManage && (
                  <Link asChild size="sm">
                    <RouterLink to={BILLING_PATH}>Upgrade</RouterLink>
                  </Link>
                )
              }
            />
          </div>
        )}

        <Tabs value={tab} onValueChange={(value) => setTab(value as TabId)}>
          <Stack gap={6}>
            <TabList aria-label="Team">
              <Tab value="members" count={members?.length}>
                Members
              </Tab>
              <Tab value="invites" count={invites.length}>
                Pending invites
              </Tab>
              <Tab value="roles">Roles & permissions</Tab>
            </TabList>

            <TabPanel value="members">
              <Stack gap={6}>
                {members ? (
                  <Stack as="ul" gap={0} aria-label="Members" className={LIST_CLASS}>
                    {members.map((member) => (
                      <MemberRow
                        key={member.userId}
                        member={member}
                        canManage={canManage}
                        onRoleChange={(role) => changeRole(member, role)}
                        onRemove={() => setRemoving(member)}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Skeleton shape="rectangle" shimmer className="h-40 rounded-lg" />
                )}
                <Well icon={ShieldCheck} title="Roles decide what people can do">
                  Editors make drafts, flows and the brand profile; reviewers approve
                  drafts before they go out. See Roles & permissions for the full list.
                </Well>
              </Stack>
            </TabPanel>

            <TabPanel value="invites">
              {invites.length > 0 ? (
                <Stack
                  as="ul"
                  gap={0}
                  aria-label="Pending invites"
                  className={LIST_CLASS}
                >
                  {invites.map((invite) => (
                    <InviteRow
                      key={invite.id}
                      invite={invite}
                      canManage={canManage}
                      onResend={() => resend(invite)}
                      onRevoke={() => revoke(invite)}
                    />
                  ))}
                </Stack>
              ) : (
                <p className="text-body-md text-text-secondary">
                  No pending invites. Invite people to see them here until they join.
                </p>
              )}
            </TabPanel>

            <TabPanel value="roles">
              <RolesTable />
            </TabPanel>
          </Stack>
        </Tabs>
      </Stack>

      <InviteModal
        open={inviting}
        onOpenChange={setInviting}
        seatsUsed={seatsUsed}
        seatLimit={plan?.seats}
        onSend={sendInvites}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Remove ${removingName} from ${workspace}?`}
        description={`${removingFirst} loses access straight away. Their drafts and flows stay in the workspace, and their seat frees up.`}
        confirmLabel={`Remove ${removingFirst}`}
        onConfirm={() => removing && remove(removing)}
      />
    </Container>
  );
}
